import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Botao, ErrorState, Modal, Nota, Skeleton } from '../../../ui';
import PainelExecucaoSync from '../../ti/PainelExecucaoSync';
import { formatarDataHora } from '../../../utils/data';
import { extrairMensagemErro } from '../../../utils/apiError';
import {
  CotaSyncStatus,
  getCotaSyncStatus,
  dispararSyncCotas,
} from '../../../api/sga-cota/sga-cotaApi';

/**
 * Sincronização automática de cotas com o SGA (at-03) + fase VINCULOS (at-05).
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * 🔴 **Mesma correção do modal de produtos: a falha da leitura deixou de ser um
 * toast a cada 3 segundos** — ver o cabeçalho de `ModalSincronizarProdutos.tsx`
 * para o raciocínio completo (`ErrorState` quando nunca houve leitura, `Nota
 * atencao` quando havia e a atualização falhou).
 *
 * 📌 O comentário original dizia *"polling enquanto houver sincronização em
 * andamento"*, mas o `setInterval` nunca checou isso — ele polla enquanto o modal
 * estiver aberto. **O comportamento foi mantido** (decisão D10 do plano) e o
 * comentário, corrigido: parar o polling ao terminar impediria de ver a execução
 * que o **cron** disparar enquanto o TI estiver com a tela aberta.
 */

const POLLING_MS = 3000;

const FASE_LABEL: Record<string, string> = {
  COTAS: 'atualizando valores das cotas',
  VINCULOS: 'reconstruindo vínculos de cotas novas',
};

interface ModalSincronizarCotasProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalSincronizarCotas: React.FC<ModalSincronizarCotasProps> = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState<CotaSyncStatus | null>(null);
  const [falhaLeitura, setFalhaLeitura] = useState('');
  const [disparando, setDisparando] = useState(false);

  const emAndamento = status?.execucao?.status === 'EM_ANDAMENTO';

  const carregar = useCallback(async () => {
    try {
      setStatus(await getCotaSyncStatus());
      setFalhaLeitura('');
    } catch (falha) {
      setFalhaLeitura(extrairMensagemErro(falha, 'Não foi possível consultar o estado agora.'));
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    setFalhaLeitura('');
    void carregar();

    const intervalo = setInterval(() => {
      void carregar();
    }, POLLING_MS);

    return () => clearInterval(intervalo);
  }, [isOpen, carregar]);

  const handleDisparar = async () => {
    setDisparando(true);
    try {
      await dispararSyncCotas();
      toast.success('Sincronização iniciada.');
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao disparar sincronização'));
    } finally {
      setDisparando(false);
    }
  };

  const execucao = status?.execucao;
  const semLeituraNenhuma = !status && !!falhaLeitura;

  return (
    <Modal
      aberto={isOpen}
      titulo="Sincronizar Cotas (SGA)"
      ocupado={disparando}
      aoFechar={onClose}
      rodape={
        <Botao variante="secundario" onClick={onClose} disabled={disparando}>
          Fechar
        </Botao>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {!status && !falhaLeitura && <Skeleton linhas={2} altura="tw-h-12" />}

        {semLeituraNenhuma && (
          <ErrorState
            mensagem="Não foi possível consultar o estado da sincronização."
            detalhe={falhaLeitura}
            aoTentarNovamente={() => void carregar()}
          />
        )}

        {status && (
          <>
            {falhaLeitura && (
              <Nota variante="atencao">
                Não foi possível atualizar o estado agora — o que está abaixo é da última leitura
                que deu certo. A tela tenta de novo sozinha a cada poucos segundos.
              </Nota>
            )}

            <div className="tw-flex tw-flex-col tw-gap-1 tw-text-sm tw-text-texto-corpo">
              <p className="tw-m-0">
                <b>Cotas locais:</b> {status.totalCotas} ({status.cotasAtivas} ativas
                {status.cotasInativas > 0 ? `, ${status.cotasInativas} inativas` : ''})
              </p>
              <p className="tw-m-0">
                <b>Última sincronização:</b>{' '}
                {status.ultimaSincronizacao ? formatarDataHora(status.ultimaSincronizacao) : 'nunca'}
              </p>
            </div>

            {execucao && (
              <PainelExecucaoSync
                status={execucao.status}
                faseRotulo={FASE_LABEL[execucao.fase] ?? execucao.fase}
                origem={execucao.origem}
                iniciadoEm={execucao.iniciadoEm}
                percentual={execucao.percentual}
                progresso={{ feitas: execucao.consultadas, total: execucao.total }}
                erro={execucao.erro}
                resumo={
                  <>
                    {execucao.atualizadas} atualizadas · {execucao.criadasSemVinculo} novas ·{' '}
                    {execucao.vinculosReconstruidos} vínculos reconstruídos · {execucao.inativadas}{' '}
                    inativadas · {execucao.falhas} falhas
                  </>
                }
                avisos={
                  execucao.vinculosPendentes > 0
                    ? [
                        <>
                          {execucao.vinculosPendentes} cota(s) sem vínculos de
                          regional/cooperativa/tipo — a reconstrução automática não as encontrou;
                          reimporte a planilha vigente para completá-los.
                        </>,
                      ]
                    : undefined
                }
              />
            )}
          </>
        )}

        <Botao
          variante="primario"
          larguraTotal
          carregando={disparando}
          disabled={emAndamento}
          onClick={handleDisparar}
        >
          {emAndamento
            ? 'Sincronização em andamento...'
            : disparando
              ? 'Disparando...'
              : 'Sincronizar agora'}
        </Botao>

        <Nota>
          Atualiza valores, faixas e situação de todas as cotas a partir do SGA. O progresso acima
          se atualiza sozinho. A sincronização também roda automaticamente todos os dias às 7h.
        </Nota>
      </div>
    </Modal>
  );
};

export default ModalSincronizarCotas;
